import { useEffect, useMemo, useState } from "react";
import { daysUntilChristmas } from "@/lib/christmas";

const STORAGE_KEY = "me_amor_xmas_confetti_v1";

// Returns true iff we're currently in December (NZ time) AND Christmas
// season is still active (not past 25 Dec).
function isDecemberInNZ() {
  const parts = new Intl.DateTimeFormat("en-NZ", {
    timeZone: "Pacific/Auckland",
    month: "2-digit",
  }).formatToParts(new Date());
  const m = Number(parts.find((p) => p.type === "month")?.value);
  return m === 12;
}

export default function HolidaySnowfall({ flakeCount = 26, duration = 8500 }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isDecemberInNZ()) return;
    const { passed } = daysUntilChristmas();
    if (passed) return;
    let played = null;
    try {
      played = window.localStorage.getItem(STORAGE_KEY);
    } catch (_e) {
      /* Safari private mode etc. */
    }
    if (played) return;
    // Respect users who don't want motion.
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mq.matches) return;

    setShow(true);
    try {
      window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch (_e) {
      /* ignore */
    }
    const t = setTimeout(() => setShow(false), duration + 500);
    return () => clearTimeout(t);
  }, [duration]);

  // Pre-compute flakes only once per mount so React doesn't re-randomise.
  const flakes = useMemo(() => {
    return Array.from({ length: flakeCount }).map((_, i) => {
      const left = Math.random() * 100; // vw
      const size = 6 + Math.random() * 12; // px
      const dur = 6 + Math.random() * 4; // s
      const delay = Math.random() * 2.5; // s
      const drift = -30 + Math.random() * 60; // px sway
      const opacity = 0.45 + Math.random() * 0.5;
      return { i, left, size, dur, delay, drift, opacity };
    });
  }, [flakeCount]);

  if (!show) return null;

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[60] overflow-hidden"
      data-testid="holiday-snowfall"
    >
      <style>{`
        @keyframes me-snowfall {
          0% { transform: translate3d(0, -10vh, 0) rotate(0deg); opacity: 0; }
          8% { opacity: var(--flake-opacity); }
          100% {
            transform: translate3d(var(--flake-drift), 110vh, 0) rotate(360deg);
            opacity: 0;
          }
        }
      `}</style>
      {flakes.map((f) => (
        <span
          key={f.i}
          className="absolute top-0 rounded-full"
          style={{
            left: `${f.left}vw`,
            width: `${f.size}px`,
            height: `${f.size}px`,
            background:
              "radial-gradient(circle at 30% 30%, #ffffff 0%, #FFF3E9 55%, rgba(255,243,233,0) 75%)",
            filter: "blur(0.4px)",
            opacity: 0,
            animation: `me-snowfall ${f.dur}s linear ${f.delay}s forwards`,
            "--flake-drift": `${f.drift}px`,
            "--flake-opacity": f.opacity,
          }}
        />
      ))}
    </div>
  );
}
